import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { getArticleById, getArticlesByCategory } from '@/mocks/articles';
import { getCategoryById } from '@/mocks/categories';

export default function Article() {
  const { articleId } = useParams<{ articleId: string }>();
  const { t } = useTranslation();

  const article = getArticleById(articleId || '');
  if (!article) {
    return (
      <>
        <Navbar />
        <ScrollToTop />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <p className="text-foreground-500">文章未找到</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm mt-4 inline-block">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = getCategoryById(article.categoryId);
  const relatedArticles = getArticlesByCategory(article.categoryId)
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: t('breadcrumbHome'), href: '/' },
    { label: category?.name || article.categoryId, href: `/category/${article.categoryId}` },
    { label: article.title },
  ];

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    datePublished: article.publishDate,
    author: { '@type': 'Organization', name: '按摩视频大全' },
    publisher: { '@type': 'Organization', name: '按摩视频大全' },
    image: article.imageUrl,
  };

  const faqJsonLd = article.faq ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  } : null;

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      {faqJsonLd && (
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      )}

      <Navbar />
      <ScrollToTop />

      <main className="pt-20">
        <div className="bg-background-100">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-10">
          {/* Header */}
          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <p className="text-base text-foreground-500 leading-relaxed mb-4">
              {article.summary}
            </p>

            <div className="flex items-center gap-4 text-sm text-foreground-400">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
              {category && (
                <Link
                  to={`/category/${category.id}`}
                  className="flex items-center gap-1 text-primary-500 hover:text-primary-600"
                >
                  <i className="ri-folder-line"></i>
                  {category.name}
                </Link>
              )}
            </div>
          </header>

          {/* Featured Image */}
          <div className="aspect-[16/9] rounded-lg overflow-hidden mb-10">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Content */}
          <div className="prose-custom mb-12">
            {article.content.map((block, index) => {
              if (block.type === 'heading') {
                const headingLevel = index === 0 ? 2 : 3;
                const HeadingTag = `h${headingLevel}` as keyof JSX.IntrinsicElements;
                return (
                  <HeadingTag
                    key={index}
                    className={`font-heading font-semibold text-foreground-950 mt-8 mb-4 ${
                      headingLevel === 2 ? 'text-xl md:text-2xl' : 'text-lg md:text-xl'
                    }`}
                  >
                    {block.text}
                  </HeadingTag>
                );
              }
              return (
                <p key={index} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  {block.text}
                </p>
              );
            })}
          </div>

          {/* FAQ */}
          {article.faq && article.faq.length > 0 && (
            <section className="mb-12 bg-background-100 rounded-lg p-5 md:p-6">
              <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-4">
                {t('faqTitle')}
              </h2>
              <div className="space-y-3">
                {article.faq.map((item, i) => (
                  <details key={i} className="group bg-background-50 rounded-lg border border-background-200/70">
                    <summary className="flex items-center justify-between p-3 md:p-4 cursor-pointer list-none">
                      <span className="text-sm font-medium text-foreground-900 pr-4">
                        {item.question}
                      </span>
                      <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                      <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                    </summary>
                    <div className="px-3 md:px-4 pb-3 md:pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="border-t border-background-200/70 pt-10">
              <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-5">
                {t('relatedArticles')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} article={ra} />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}